import { t } from 'i18n';

const BASE = 'frontend.lesson_task.tasks.check.assignment.recorder_errors';

/**
 * Maps a raw recorder error string — a capacitor-voice-recorder plugin code, a
 * DOMException name, or a hook message — to a localized, user-facing message.
 * Unknown errors fall back to a generic "try again" message.
 *
 * The recorder hooks keep emitting raw strings (no onError contract change);
 * this mapping is applied only at the display layer (AudioRecorder.tsx). Keep
 * the matched substrings in sync with what the hooks actually emit.
 */
export function recorderErrorMessage(raw: string): string {
  const r = (raw || '').toLowerCase();
  const match = (...needles: string[]) => needles.some((n) => r.includes(n));

  if (
    match(
      'missing_permission',
      'permission not granted',
      'notallowederror',
      'permission denied'
    )
  ) {
    // @tolgee-ignore
    return t(`${BASE}.permission_denied`, {
      defaultValue:
        'Microphone access is blocked. Enable microphone permission in your settings and try again.'
    });
  }

  if (match('already_recording')) {
    // @tolgee-ignore
    return t(`${BASE}.already_recording`, {
      defaultValue:
        'A recording is already in progress. Please restart the app and try again.'
    });
  }

  if (match('device_cannot_voice_record', 'device cannot record', 'notfounderror')) {
    // @tolgee-ignore
    return t(`${BASE}.device_unsupported`, {
      defaultValue: 'No microphone was found on this device.'
    });
  }

  if (match('notreadableerror', 'microphone_being_used', 'could not start audio')) {
    // @tolgee-ignore
    return t(`${BASE}.device_busy`, {
      defaultValue:
        'The microphone is busy or in use by another app. Close other apps and try again.'
    });
  }

  if (match('not available', 'https scheme')) {
    // @tolgee-ignore
    return t(`${BASE}.unavailable`, {
      defaultValue:
        'Audio recording is not available here. Check your microphone permissions and connection, then try again.'
    });
  }

  if (match('empty_recording', 'no audio data', 'produced no audio')) {
    // @tolgee-ignore
    return t(`${BASE}.empty_recording`, {
      defaultValue: 'We didn’t catch any audio. Please try recording again.'
    });
  }

  // @tolgee-ignore
  return t(`${BASE}.generic`, {
    defaultValue: 'Couldn’t start recording. Please try again.'
  });
}
